import axios from 'axios';
import ErrorHandler from '../utils/errorHandler';
import browserCacheService from './BrowserCacheService';
import { clearAuthStorage } from '../utils/auth';
import { AURA_CLIENT_HEADERS } from '../utils/auraClient';

// Nach Session-Mutationen die gecachten Session-Listen verwerfen, damit die
// SessionsPage beim nächsten Aufruf keinen veralteten Stand aus dem Cache zeigt
const invalidateSessionsCache = () => {
  try {
    browserCacheService.invalidateSessions();
  } catch (error) {
    console.warn('Session-Cache konnte nicht invalidiert werden:', error.message);
  }
};

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api/v1`
    : (process.env.REACT_APP_API_URL || 'http://localhost:5002/api/v1'),
  timeout: 30000,  // 30 Sekunden für AI-Calls (Goal Suggestions)
  headers: {
    'Content-Type': 'application/json',
    ...AURA_CLIENT_HEADERS
  }
});

// Hilfsfunktion um zu prüfen, ob eine Session-ID lokal ist
const isLocalSession = (sessionId) => {
  return sessionId && typeof sessionId === 'string' && sessionId.startsWith('local-');
};

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    // Füge Auth-Token hinzu, wenn vorhanden
    const token = localStorage.getItem('token');
    if (token) {
      // Einfache Validierung des Tokens
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.error('Ungültiges Token-Format im localStorage, Token wird entfernt');
          localStorage.removeItem('token');
          throw new Error('Ungültiges Token');
        }
        
        // Prüfe ob Token abgelaufen ist
        const payloadBase64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        try {
          const payload = JSON.parse(atob(payloadBase64));
          const expiry = payload.exp * 1000; // In Millisekunden umwandeln

          if (expiry && Date.now() > expiry) {
            // Token ist abgelaufen, aber innerhalb des 7-Tage-Refresh-Fensters des
            // Backends noch erneuerbar. NICHT aus dem localStorage entfernen — sonst
            // findet der Response-Interceptor beim folgenden 401 kein Token mehr für
            // den Refresh (localStorage.getItem('token') === null) und der User wird
            // ausgeloggt, statt die Sitzung nahtlos zu erneuern.
            console.warn('Token ist abgelaufen - wird zum Refresh mitgesendet, nicht entfernt');
          }

          // Token erscheint gültig, füge es hinzu
      config.headers.Authorization = `Bearer ${token}`;
          
      // Debug-Log zum Erkennen von Token-Problemen
      console.log(`API Request: ${config.method.toUpperCase()} ${config.url} - Token vorhanden`);
        } catch (decodeError) {
          console.error('Fehler beim Dekodieren des Tokens:', decodeError);
          // Verwende den Token trotzdem, falls der Server es validieren kann
          config.headers.Authorization = `Bearer ${token}`;
          console.warn(`API Request mit möglicherweise ungültigem Token: ${config.method.toUpperCase()} ${config.url}`);
        }
      } catch (error) {
        console.warn(`Token-Validierungsfehler: ${error.message}`);
        // Verwende config ohne Token, wenn es ungültig ist
      }
    } else {
      console.warn(`API Request ohne Token: ${config.method.toUpperCase()} ${config.url}`);
    }
    
    // Füge SchoolId hinzu, wenn vorhanden
    const schoolId = localStorage.getItem('schoolId');
    if (schoolId) {
      config.headers['X-School-ID'] = schoolId;
    }
    
    // Füge UserId hinzu, wenn vorhanden
    const userId = localStorage.getItem('userId');
    if (userId) {
      config.headers['X-User-ID'] = userId;
    }
    
    // Demo-Modus aktivieren, wenn entsprechend gesetzt
    const isDemo = localStorage.getItem('isDemo') === 'true';
    if (isDemo) {
      config.headers['X-Demo-Mode'] = 'true';
    }
    
    // Prüfe, ob es sich um eine lokale Session-Anfrage handelt und simuliere eine Antwort
    const url = config.url;
    if (url && url.includes('/sessions/local-')) {
      const sessionId = url.split('/sessions/')[1].split('/')[0];
      if (isLocalSession(sessionId)) {
        console.log(`Lokale Session erkannt: ${sessionId}, simuliere API-Antwort`);
        // Abbrechen der echten Anfrage - wir werden eine simulierte Antwort zurückgeben
        return Promise.reject({
          __isLocalSession: true,
          sessionId,
          url,
          method: config.method,
          data: config.data
        });
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag um parallele Refresh-Versuche zu verhindern
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response Interceptor mit automatischem Token-Refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Prüfe, ob es sich um eine simulierte lokale Session-Anfrage handelt
    if (error && error.__isLocalSession) {
      console.log(`Simuliere Antwort für lokale Session: ${error.url}`);
      return simulateLocalSessionResponse(error);
    }

    const originalRequest = error.config;

    // Spezielle Behandlung für Authentifizierungsfehler (401)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Nicht bei Login/Register/Refresh Routen
      const isAuthRoute = originalRequest?.url?.includes('/auth/login')
                       || originalRequest?.url?.includes('/auth/register')
                       || originalRequest?.url?.includes('/auth/refresh');

      if (isAuthRoute) {
        return Promise.reject(error);
      }

      console.warn('401 Fehler erkannt - versuche Token-Refresh', {
        url: originalRequest?.url
      });

      // Wenn bereits ein Refresh läuft, warte auf das Ergebnis
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const token = localStorage.getItem('token');

      if (!token) {
        isRefreshing = false;
        processQueue(new Error('Kein Token vorhanden'), null);
        return Promise.reject(error);
      }

      try {
        // Versuche Token zu erneuern
        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { token },
          {
            headers: { 'Content-Type': 'application/json', ...AURA_CLIENT_HEADERS },
            timeout: 10000
          }
        );

        if (response.data?.token) {
          const newToken = response.data.token;

          // Speichere neues Token
          localStorage.setItem('token', newToken);
          localStorage.setItem('token_set_time', Date.now().toString());

          console.log('Token erfolgreich erneuert');

          // Aktualisiere Authorization Header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Verarbeite wartende Requests
          processQueue(null, newToken);
          isRefreshing = false;

          // Wiederhole den ursprünglichen Request
          return api(originalRequest);
        } else {
          // Refresh lieferte 200 ohne Token (z.B. { success: false }). Ohne diesen
          // Zweig blieben isRefreshing=true und die failedQueue dauerhaft hängen:
          // jeder weitere 401 würde sich unendlich in der Queue aufhängen (Deadlock,
          // wartende Promises werden nie aufgelöst). State zurücksetzen und ausloggen.
          console.error('Token-Refresh: Antwort ohne Token erhalten');
          // Nicht nur das Token: auch user/userId/schoolId. Sonst hydriert der nächste
          // Nutzer auf demselben Gerät das Profil des vorigen (AuthContext liest localStorage).
          clearAuthStorage();
          processQueue(new Error('Leere Refresh-Response'), null);
          isRefreshing = false;

          const authErrorEvent = new CustomEvent('auth:error', {
            detail: {
              message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
              source: 'api',
              url: originalRequest?.url
            }
          });
          window.dispatchEvent(authErrorEvent);

          return Promise.reject(new Error('Leere Refresh-Response'));
        }
      } catch (refreshError) {
        console.error('Token-Refresh fehlgeschlagen:', refreshError.message);

        // Refresh fehlgeschlagen - komplette Auth-Daten entfernen und User ausloggen
        clearAuthStorage();
        processQueue(refreshError, null);
        isRefreshing = false;

        // Globales Auth-Error-Event auslösen
        const authErrorEvent = new CustomEvent('auth:error', {
          detail: {
            message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
            source: 'api',
            url: originalRequest?.url
          }
        });
        window.dispatchEvent(authErrorEvent);

        return Promise.reject(refreshError);
      }
    }

    // Best-Effort-/Hintergrund-Calls (z.B. optionale KI-Vorbefuellung) duerfen
    // keinen globalen Fehler-Toast ausloesen - sie setzen skipErrorToast.
    if (error.config?.skipErrorToast) {
      return Promise.reject(error);
    }

    // Behandle den Fehler mit unserem ErrorHandler
    ErrorHandler.handle(error);
    return Promise.reject(error);
  }
);

// Request-Deduplizierung für GET-Anfragen.
// Hintergrund: Beim App-Resume/Reconnect feuern mehrere Komponenten/Effekte denselben
// GET (z.B. /unread-counts, /participants) 2-4x gleichzeitig ab. Identische gleichzeitige
// GETs teilen sich hier eine einzige Promise -> ein Netzwerk-Request statt vieler.
const inFlightGets = new Map();
const originalGet = api.get.bind(api);
api.get = (url, config = {}) => {
  // Anfragen mit AbortController-Signal nicht deduplizieren (ein Abbruch würde sonst
  // den geteilten Request für alle anderen abbrechen).
  if (config.signal) {
    return originalGet(url, config);
  }

  const key = `${url}::${JSON.stringify(config.params || {})}`;
  const existing = inFlightGets.get(key);
  if (existing) {
    return existing;
  }

  const promise = originalGet(url, config).finally(() => {
    inFlightGets.delete(key);
  });
  inFlightGets.set(key, promise);
  return promise;
};

// Simuliere Antworten für lokale Sessions
const simulateLocalSessionResponse = (request) => {
  const { sessionId, url, method, data } = request;
  
  // Hole die aktuelle Session aus dem localStorage
  const sessionJSON = localStorage.getItem('currentSession');
  if (!sessionJSON) {
    return Promise.reject(new Error('Keine lokale Session gefunden'));
  }
  
  const session = JSON.parse(sessionJSON);
  const userId = localStorage.getItem('userId');
  
  // Simuliere verschiedene Endpunkte
  if (url.includes('/heartbeat')) {
    return Promise.resolve({
      data: {
        success: true,
        timestamp: new Date().toISOString(),
        participant: {
          lastHeartbeat: new Date().toISOString(),
          focusTime: 0,
          focusStats: {}
        }
      }
    });
  }
  
  if (url.includes('/focus')) {
    const focusData = method === 'POST' ? data : {};
    return Promise.resolve({
      data: {
        sessionId,
        userId,
        totalFocusTime: (focusData?.focusTimeMs || 0),
        focusPercentage: 0.8,
        lastUpdate: new Date().toISOString()
      }
    });
  }
  
  if (url.includes('/participants')) {
    return Promise.resolve({
      data: session.participants || []
    });
  }
  
  // Fallback für andere Endpunkte
  return Promise.resolve({
    data: session
  });
};

// Einladungs-API-Funktionen
const getInvitations = async () => {
  try {
    const response = await api.get('/invitations');
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

const createInvitation = async (invitationData) => {
  try {
    const response = await api.post('/invitations', invitationData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

const deleteInvitation = async (invitationId) => {
  try {
    const response = await api.delete(`/invitations/${invitationId}`);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

const getInvitationQR = async (invitationId, format = 'dataurl', size = 400) => {
  try {
    const response = await api.get(`/invitations/${invitationId}/qr`, {
      params: { format, size },
    });
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

// API-Methoden
export const apiService = {
  // Auth
  async login(credentials) {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Profile
  async getProfile() {
    try {
      const response = await api.get('/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateProfile(data) {
    try {
      const response = await api.put('/profile', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Sessions
  async getSessions(params = {}) {
    try {
      const response = await api.get('/sessions', { params });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der Sessions:', error);
      throw error;
    }
  },

  // Skills
  async getSchoolSkills(schoolId) {
    try {
      const response = await api.get(`/skills/school/${schoolId}`);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der Skills:', error);
      throw error;
    }
  },

  // skipErrorToast: fuer Aufrufer, die den Fehler selbst anzeigen (der Wizard
  // zeigt ihn als Hinweis) — sonst stand dieselbe Meldung doppelt da.
  async createSession(sessionData, { skipErrorToast = false } = {}) {
    try {
      // Token-Prüfung vor dem API-Aufruf
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Fehler beim Erstellen der Session: Kein Auth-Token vorhanden');
        throw new Error('Bitte melde dich erneut an, um eine Session zu erstellen');
      }

      console.log('Sende Session-Erstellung an:', api.defaults.baseURL + '/sessions');
      console.log('Sende Session-Daten:', JSON.stringify(sessionData));
      
      // Korrekten Endpunkt basierend auf ENV verwenden
      const endpoint = '/sessions';
      const response = skipErrorToast
        ? await api.post(endpoint, sessionData, { skipErrorToast: true })
        : await api.post(endpoint, sessionData);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      console.error('API Fehler beim Erstellen der Session:', error);
      
      // Detaillierte Fehlerinformationen extrahieren
      let errorMsg = 'Fehler beim Erstellen der Session';
      if (error.response) {
        console.error('Server-Antwort:', error.response.data);
        errorMsg = error.response.data.error || error.response.data.message || errorMsg;
      }
      
      throw new Error(errorMsg);
    }
  },

  async joinSession(sessionId, code, joinContext) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session-Logik
        const sessionJSON = localStorage.getItem('currentSession');
        if (!sessionJSON) return null;
        const session = JSON.parse(sessionJSON);
        return session;
      }

      // joinContext (Forschung): Provenienz der Teilnahme, z.B. 'OPEN_LIST' | 'QR' | 'CODE'
      const response = await api.post(`/sessions/${sessionId}/join`, { code, joinContext });
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async leaveSession(sessionId) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session-Logik
        localStorage.removeItem('currentSession');
        return { success: true };
      }
      
      const response = await api.post(`/sessions/${sessionId}/leave`);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ✅ Validiert einen Teilnehmer mittels Session-Code
   * Nutzt POST /sessions/:sessionId/validate (für Teilnehmer, nicht Host!)
   */
  async validateParticipant(sessionId, code) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session - keine echte Validierung nötig
        return { success: true, message: 'Lokale Session validiert' };
      }
      
      const response = await api.post(`/sessions/${sessionId}/validate`, { code });
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      console.error('API Error validating participant:', error);
      throw error;
    }
  },

  // background: Poll/Refetch einer Session, die schon auf dem Bildschirm steht.
  // Dann kein globaler Fehler-Toast - der Aufrufer zeigt selbst einen Stale-Hinweis.
  // Sonst kaeme bei 502/503 (Backend-Neustart beim Deploy) oder 429 alle 15 s
  // "Ein unerwarteter Fehler ist aufgetreten." (Audit A29).
  async getSessionDetails(sessionId, { background = false } = {}) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session-Logik
        const localSession = JSON.parse(localStorage.getItem('currentSession') || '{}');
        return { success: true, data: localSession };
      }

      const response = background
        ? await api.get(`/sessions/${sessionId}`, { skipErrorToast: true })
        : await api.get(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getMyActiveSession() {
    try {
      const response = await api.get('/sessions/my-active-session');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getSessionLimits() {
    try {
      const response = await api.get('/sessions/limits');
      return response.data;
    } catch (error) {
      return { success: true, limits: { maxParticipants: 30, maxDuration: 90, sessionsPerHour: 5 } };
    }
  },

  async updateSessionLimits(schoolId, limits) {
    try {
      const response = await api.patch(`/admin/schools/${schoolId}/session-limits`, limits);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async startSession(sessionId) {
    try {
      console.log('🌐 API: Starting session:', sessionId);
      const response = await api.post(`/sessions/${sessionId}/start`);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      console.error('API Error starting session:', error);
      throw error;
    }
  },

  async exportSessionsIcal() {
    try {
      const response = await api.get('/sessions/export/ical', {
        responseType: 'text',
        transformResponse: [(data) => data],
      });
      return response.data;
    } catch (error) {
      console.error('API Error exporting iCal:', error);
      throw error;
    }
  },

  async completeSession(sessionId, feedbackData = {}) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session-Logik
        localStorage.removeItem('currentSession');
        return { success: true, completed: true };
      }

      console.log('🌐 API: completeSession called with feedback', {
        sessionId,
        hasFeedback: Object.keys(feedbackData).length > 0,
        feedbackKeys: Object.keys(feedbackData),
      });

      // Sende Feedback-Daten im Body
      const response = await api.post(`/sessions/${sessionId}/complete`, feedbackData);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async submitSessionFeedback(sessionId, feedbackData) {
    try {
      console.log('🌐 API: submitSessionFeedback called', {
        sessionId,
        endpoint: `/sessions/${sessionId}/feedback`,
        payloadKeys: Object.keys(feedbackData),
        hasGoals: !!feedbackData.goals,
        goalsCount: feedbackData.goals ? feedbackData.goals.length : 0
      });

      const response = await api.post(`/sessions/${sessionId}/feedback`, feedbackData);

      console.log('✅ API: Feedback submission response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API: Error submitting session feedback:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // KI-Live-Quiz
  async startQuiz(sessionId, mode = null) {
    const response = await api.post(`/sessions/${sessionId}/quiz`, mode ? { mode } : {});
    return response.data;
  },
  async claimQuizAnswer(sessionId, roundId, quizMode = 'competitive') {
    const response = await api.post(`/sessions/${sessionId}/quiz/${roundId}/claim`, { quizMode });
    return response.data;
  },
  async submitSoloAnswer(sessionId, roundId, answerText) {
    const response = await api.post(`/sessions/${sessionId}/quiz/${roundId}/answer`, { answerText });
    return response.data;
  },
  async getQuizState(sessionId) {
    const response = await api.get(`/sessions/${sessionId}/quiz/current`);
    return response.data;
  },
  async getQuizStats(sessionId) {
    const response = await api.get(`/sessions/${sessionId}/quiz/stats`);
    return response.data;
  },

  // TeachOtter KI-Lerncoach
  async sendCoachMessage(sessionId, message, conversationHistory = []) {
    const response = await api.post(`/sessions/${sessionId}/coach/chat`, {
      message,
      conversationHistory: conversationHistory.slice(-20),
    });
    return response.data;
  },

  async getCoachContext(sessionId) {
    const response = await api.get(`/sessions/${sessionId}/coach/context`);
    return response.data;
  },

  // Antwort auf eine Inline-Quizfrage des TeachOtter auswerten lassen
  async submitCoachQuizAnswer(sessionId, quizId, answerIndex) {
    const response = await api.post(`/sessions/${sessionId}/coach/quiz/answer`, {
      quizId,
      answerIndex,
    });
    return response.data;
  },

  // Learning Journal API Functions
  async getLearningJournalAnalytics() {
    try {
      const response = await api.get('/learning-journal/analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching learning journal analytics:', error);
      throw error;
    }
  },

  async getLearningJournalSessions() {
    try {
      const response = await api.get('/learning-journal/sessions');
      return response.data;
    } catch (error) {
      console.error('Error fetching learning journal sessions:', error);
      throw error;
    }
  },

  // Offene Logbuch-Entwuerfe (z.B. nach Auto-Complete) - speist den Banner
  async getLearningJournalDrafts() {
    const response = await api.get('/learning-journal/drafts');
    return response.data;
  },

  // Manuelle Logbucheintraege ohne Session (Lernlogbuch 2.0)
  async getJournalEntries() {
    const response = await api.get('/learning-journal/entries');
    return response.data;
  },

  async createJournalEntry(entryData) {
    const response = await api.post('/learning-journal/entries', entryData);
    return response.data;
  },

  async updateJournalEntry(entryId, entryData) {
    const response = await api.put(`/learning-journal/entries/${entryId}`, entryData);
    return response.data;
  },

  async deleteJournalEntry(entryId) {
    const response = await api.delete(`/learning-journal/entries/${entryId}`);
    return response.data;
  },

  // Lernlogbuch per Link teilen (ganzes Logbuch, schreibgeschuetzt, ohne Login)
  async createJournalShare({ expiresIn = '7d' } = {}) {
    const response = await api.post('/learning-journal/share', { expiresIn });
    return response.data;
  },

  async listJournalShares() {
    const response = await api.get('/learning-journal/shares');
    return response.data;
  },

  async deleteJournalShare(token) {
    const response = await api.delete(`/learning-journal/share/${encodeURIComponent(token)}`);
    return response.data;
  },

  // Oeffentlich: ohne Token im Storage setzt der Request-Interceptor schlicht
  // keinen Authorization-Header - genau richtig fuer die geteilte Ansicht.
  async getSharedJournal(token) {
    const response = await api.get(`/learning-journal/shared/${encodeURIComponent(token)}`);
    return response.data;
  },

  // Personal Access Tokens (PAT) fuer den MCP-/API-Zugang
  async getApiTokens() {
    const response = await api.get('/auth/tokens');
    return response.data;
  },

  async createApiToken(tokenData) {
    const response = await api.post('/auth/tokens', tokenData);
    return response.data;
  },

  async deleteApiToken(tokenId) {
    const response = await api.delete(`/auth/tokens/${tokenId}`);
    return response.data;
  },

  async uploadJournalEntryFiles(entryId, formData) {
    const response = await api.post(`/learning-journal/entries/${entryId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteJournalEntryFile(entryId, fileId) {
    const response = await api.delete(`/learning-journal/entries/${entryId}/files/${fileId}`);
    return response.data;
  },

  // Eigenes Session-Feedback komplett laden (zum nachtraeglichen Bearbeiten)
  async getSessionFeedback(sessionId) {
    const response = await api.get(`/sessions/${sessionId}/feedback`, { skipErrorToast: true });
    return response.data;
  },

  // Eigenen Logbucheintrag (Feedback) einer Session loeschen
  async deleteSessionFeedback(sessionId) {
    const response = await api.delete(`/sessions/${sessionId}/feedback`);
    return response.data;
  },

  // KI-Vorschlag fuer das Feedback-Formular (Lernlogbuch 2.0)
  // Best-Effort: scheitert er, soll der Nutzer keinen Fehler-Toast sehen.
  async getSessionFeedbackDraft(sessionId) {
    const response = await api.get(`/sessions/${sessionId}/feedback/draft`, { skipErrorToast: true });
    return response.data;
  },

  // KI-Lernzusammenfassung anstossen (beim Oeffnen des Feedback-Formulars)
  // Best-Effort: scheitert er, soll der Nutzer keinen Fehler-Toast sehen.
  async generateSessionFeedbackDraft(sessionId) {
    const response = await api.post(`/sessions/${sessionId}/feedback/draft/generate`, undefined, { skipErrorToast: true });
    return response.data;
  },

  async getLearningJournalInsights() {
    try {
      const response = await api.get('/learning-journal/ai-insights');
      return response.data;
    } catch (error) {
      console.error('Error fetching learning journal insights:', error);
      throw error;
    }
  },

  async getLearningJournalGoals(filters = {}) {
    try {
      // Nutze den neuen direkten Goals Endpoint
      const params = new URLSearchParams(filters);
      const response = await api.get(`/learning-journal/goals?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching learning journal goals:', error);
      // Fallback auf Analytics Endpoint
      try {
        const analyticsResponse = await api.get('/learning-journal/analytics');
        if (analyticsResponse.data && analyticsResponse.data.data && analyticsResponse.data.data.goals) {
          return {
            success: true,
            data: {
              goals: analyticsResponse.data.data.goals.recent || [],
              stats: analyticsResponse.data.data.goals
            }
          };
        }
        return { success: true, data: { goals: [], stats: {} } };
      } catch (fallbackError) {
        throw error;
      }
    }
  },

  async cancelSession(sessionId) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session-Logik
        localStorage.removeItem('currentSession');
        return { success: true, cancelled: true };
      }

      const response = await api.post(`/sessions/${sessionId}/cancel`);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteSession(sessionId) {
    try {
      const response = await api.delete(`/sessions/${sessionId}`);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      console.error('Fehler beim Löschen der Session:', error);
      throw error;
    }
  },

  async updateSession(sessionId, data) {
    try {
      if (isLocalSession(sessionId)) {
        // Lokale Session-Logik
        const localSession = JSON.parse(localStorage.getItem('currentSession') || '{}');
        const updatedSession = { ...localSession, ...data };
        localStorage.setItem('currentSession', JSON.stringify(updatedSession));
        return { success: true, data: updatedSession };
      }

      const response = await api.patch(`/sessions/${sessionId}`, data);
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateSessionFocus(sessionId, focusData) {
    try {
      if (isLocalSession(sessionId)) {
        return simulateLocalSessionResponse({
          sessionId,
          url: `/sessions/${sessionId}/focus`,
          method: 'POST',
          data: focusData
        }).then(res => res.data);
      }

      const response = await api.post(`/sessions/${sessionId}/focus`, focusData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * ✅ NEU: Sendet Focus-Heartbeat für Group Sessions (30s Intervall)
   *
   * Unterschied zu updateSessionFocus():
   * - Nutzt /api/v1/focus/heartbeat (nicht /sessions/:id/focus)
   * - Gibt teamFocusPercentage zurück (für GroupFocusTracker)
   * - Für Group Sessions optimiert (30s statt 5s Updates)
   *
   * @param {string} sessionId - Die Session-ID
   * @param {Object} data - { isFocused: boolean, focusTimeMs: number, timestamp: Date }
   * @returns {Promise<{ success: boolean, data: { teamFocusPercentage: number, ... } }>}
   */
  async sendFocusHeartbeat(sessionId, data) {
    try {
      if (isLocalSession(sessionId)) {
        return simulateLocalSessionResponse({
          sessionId,
          url: `/focus/heartbeat`,
          method: 'POST',
          data
        }).then(res => ({
          success: true,
          data: {
            ...res.data,
            teamFocusPercentage: Math.round(Math.random() * 30 + 70) // Simulate 70-100%
          }
        }));
      }

      const response = await api.post('/api/v1/focus/heartbeat', {
        sessionId,
        ...data
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateSessionHeartbeat(sessionId, data) {
    try {
      const response = await api.post(`/sessions/${sessionId}/heartbeat`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async sessionHeartbeat(sessionId) {
    try {
      if (isLocalSession(sessionId)) {
        return simulateLocalSessionResponse({
          sessionId,
          url: `/sessions/${sessionId}/heartbeat`,
          method: 'POST'
        }).then(res => res.data);
      }
      
      // Hintergrund-Call: Ein Fehler hier (z.B. 404 fuer eine veraltete Session aus dem
      // localStorage) darf keinen globalen Toast "Session nicht gefunden" ausloesen -
      // der SessionManager raeumt in dem Fall selbst auf.
      const response = await api.post(`/sessions/${sessionId}/heartbeat`, undefined, { skipErrorToast: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async validateSessionToken(sessionId, joinToken) {
    try {
      const response = await api.post(`/sessions/validate-token`, {
        sessionId,
        joinToken
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // skipErrorToast: fuer Hintergrund-Pruefungen (Heartbeat, gemerkte Session).
  // Ein 403/404 heisst dort nur „nicht mehr dabei“ und wird still aufgeraeumt.
  async getSessionParticipants(sessionId, { skipErrorToast = false } = {}) {
    try {
      if (isLocalSession(sessionId)) {
        return simulateLocalSessionResponse({
          sessionId,
          url: `/sessions/${sessionId}/participants`,
          method: 'GET'
        }).then(res => res.data);
      }
      
      const url = `/sessions/${sessionId}/participants`;
      const response = skipErrorToast
        ? await api.get(url, { skipErrorToast: true })
        : await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async removeParticipant(sessionId, userId) {
    try {
      const response = await api.delete(`/sessions/${sessionId}/participants/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Entfernen des Teilnehmers:', error);
      throw error;
    }
  },

  // Host laesst einen entfernten Teilnehmer wieder zu (Un-Kick nach Versehen)
  async readmitParticipant(sessionId, userId) {
    try {
      const response = await api.post(`/sessions/${sessionId}/participants/${userId}/readmit`);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Wieder-Zulassen des Teilnehmers:', error);
      throw error;
    }
  },

  // Host markiert einen Teilnehmer manuell als anwesend (z.B. Schueler ohne eigenes Geraet)
  async markParticipantPresent(sessionId, userId) {
    try {
      const response = await api.post(`/sessions/${sessionId}/participants/${userId}/mark-present`);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Markieren des Teilnehmers als anwesend:', error);
      throw error;
    }
  },

  async reconnectToSession(sessionId, reconnectData) {
    try {
      const response = await api.post(`/sessions/${sessionId}/reconnect`, reconnectData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Aura
  async getAuraStats() {
    try {
      const response = await api.get('/aura/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // getAuraTransactions()/claimDailyLogin() entfernt (Sep 2026): zeigten auf
  // Endpunkte, die es nie gab (/aura/transactions, /aura/daily-login). Der
  // Verlauf laeuft ueber GET /aura/history, der Login-Bonus automatisch im Backend.

  // Skills
  async getSkills() {
    try {
      const response = await api.get('/skills');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async suggestSkill(skillData) {
    try {
      // ✅ FIX: Korrekter Endpunkt ist /skills/request, nicht /skills/suggest
      const response = await api.post('/skills/request', skillData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Schools
  async registerSchool(schoolData) {
    try {
      const response = await api.post('/schools/register', schoolData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async checkSchoolLicense(domain) {
    try {
      const response = await api.post('/schools/check-license', { domain });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getOrCreateSchool(schoolData) {
    try {
      const response = await api.post('/schools/get-or-create', schoolData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async confirmSchoolRegistration(token, schoolId) {
    try {
      const response = await api.post('/schools/confirm', { token, schoolId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Matching API
  async getMatchScore(user1Id, user2Id) {
    try {
      const response = await api.get(`/matching/score/${user1Id}/${user2Id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getPotentialMatches(userId, filters = {}) {
    try {
      const response = await api.get(`/matching/potential/${userId}`, { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateMatchingPreferences(userId, preferences) {
    try {
      const response = await api.put(`/matching/preferences/${userId}`, preferences);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Session-bezogene API-Calls
  // source (Forschung): 'qr' (Scan) oder 'manual' (Code-Eingabe) - Provenienz der Teilnahme
  verifySessionCode: async function(code, source) {
    try {
      const response = await api.post(
        `/sessions/verify-code`,
        { code, source },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      invalidateSessionsCache();
      return response.data;
    } catch (error) {
      ErrorHandler.handle(error);
      throw error;
    }
  },

  getSessionQRCode: async function(sessionId) {
    try {
      const response = await api.get(`/sessions/${sessionId}/qr-code`);
      return response.data;
    } catch (error) {
      ErrorHandler.handle(error);
      throw error;
    }
  },

  // Invitation APIs
  getInvitations,
  createInvitation,
  deleteInvitation,
  getInvitationQR,
  
  // CSV Import APIs
  async uploadCsvFile(formData) {
    try {
      const response = await api.post('/csvimport/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async confirmCsvImport(data) {
    try {
      const response = await api.post('/csvimport/confirm', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async downloadCsvTemplate() {
    try {
      const response = await api.get('/csvimport/template', {
        responseType: 'text'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Smart Search API - STUFE 1 Implementierung
  async smartSearch(searchData) {
    try {
      console.log('🔍 Smart Search API called with:', searchData);
      
      const response = await api.post('/profile/search', {
        searchTerm: searchData.searchTerm || '',
        filters: searchData.filters || {},
        page: searchData.page || 1,
        limit: searchData.limit || 20
      });
      
      console.log('✅ Smart Search Response:', {
        profileCount: response.data.profiles?.length || 0,
        hasMore: response.data.hasMore,
        totalCount: response.data.totalCount
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Smart Search API Error:', error);
      throw error;
    }
  },

  // STUFE 2: Infinite Scroll API für MainScreen
  async getTopMatches(page = 1, limit = 20, filters = {}) {
    try {
      console.log('🎯 Top Matches API called with:', { page, limit, filters });
      
      const response = await api.get('/profiles/discover', {
        params: {
          page,
          limit,
          minScore: 0, // Ermöglicht Profile-Discovery
          ...filters
        }
      });
      
      console.log('✅ Top Matches Response:', {
        profileCount: response.data.data?.profiles?.length || 0,
        hasMore: response.data.data?.pagination?.hasMore,
        totalCount: response.data.data?.pagination?.totalCount,
        page: response.data.data?.pagination?.page
      });
      
      // Normalisiere die Antwort für einheitliche Verwendung
      return {
        profiles: response.data.data?.profiles || [],
        hasMore: response.data.data?.pagination?.hasMore || false,
        totalCount: response.data.data?.pagination?.totalCount || 0,
        page: response.data.data?.pagination?.page || page,
        cached: response.data.data?.meta?.cached || false
      };
    } catch (error) {
      console.error('❌ Top Matches API Error:', error);
      throw error;
    }
  },

  // Connected Learning Partners (Matches)
  async getConnectedPartners() {
    try {
      const response = await api.get('/sessions/connected-partners');
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der verbundenen Lernpartner:', error);
      throw error;
    }
  },

  // Private Session mit Match-Einladungen
  async createPrivateSessionWithPartners(sessionData, invitedPartnerIds = [], { skipErrorToast = false } = {}) {
    try {
      const body = { sessionData, invitedPartnerIds };
      const response = skipErrorToast
        ? await api.post('/sessions/private-with-partners', body, { skipErrorToast: true })
        : await api.post('/sessions/private-with-partners', body);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Erstellen der privaten Session:', error);
      // Wie createSession: den Text des Backends weitergeben (Stundenlimit,
      // schon in einer laufenden Session, nicht mehr vernetzt). Bis Okt 2026
      // zeigte der Wizard hier „Request failed with status code 400“.
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      if (!serverMessage) throw error;
      const wrapped = new Error(serverMessage);
      wrapped.response = error.response;
      throw wrapped;
    }
  },

  // School Users (für Admin Dashboard)
  async getSchoolUsers(schoolId, params = {}) {
    try {
      const response = await api.get(`/admin/schools/${schoolId}/users`, { params });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der School Users:', error);
      throw error;
    }
  },

  // ── Barcamp / Projekt-Planer (Admin + Lehrer) ──────────────────
  async bulkCreateSessions(payload) {
    try {
      const response = await api.post('/sessions/bulk', payload);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.error || 'Sessions konnten nicht erstellt werden';
      throw new Error(msg);
    }
  },

  async saveBarcampDraft(payload) {
    try {
      const response = await api.post('/sessions/barcamp/drafts', payload);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.error || 'Entwurf konnte nicht gespeichert werden';
      throw new Error(msg);
    }
  },

  async listBarcampDrafts() {
    try {
      const response = await api.get('/sessions/barcamp/drafts');
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der Barcamp-Entwürfe:', error);
      throw error;
    }
  },

  async getBarcampDraft(id) {
    try {
      const response = await api.get(`/sessions/barcamp/drafts/${id}`);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.error || 'Entwurf konnte nicht geladen werden';
      throw new Error(msg);
    }
  },

  async deleteBarcampDraft(id) {
    try {
      const response = await api.delete(`/sessions/barcamp/drafts/${id}`);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.error || 'Entwurf konnte nicht gelöscht werden';
      throw new Error(msg);
    }
  }
};

// Skills API
export const updateSkill = async (skillId, skillData) => {
  try {
    const response = await api.patch(`/api/v1/skills/${skillId}`, skillData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const deleteSkill = async (skillId) => {
  try {
    const response = await api.delete(`/api/v1/skills/${skillId}`);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const createSkill = async (skillData) => {
  try {
    const response = await api.post('/api/v1/skills', skillData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

// Goal Management API
export const getSessionGoals = async (sessionId) => {
  try {
    const response = await api.get(`/api/v1/sessions/${sessionId}/goals`);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const createSessionGoal = async (sessionId, goalData) => {
  try {
    const response = await api.post(`/sessions/${sessionId}/goals`, goalData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const updateSessionGoal = async (sessionId, goalId, updateData) => {
  try {
    const response = await api.put(`/sessions/${sessionId}/goals/${goalId}`, updateData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const deleteSessionGoal = async (sessionId, goalId) => {
  try {
    const response = await api.delete(`/sessions/${sessionId}/goals/${goalId}`);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const updateSessionGoalsBatch = async (sessionId, goalsData) => {
  try {
    const response = await api.post(`/sessions/${sessionId}/goals/batch`, { goals: goalsData });
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

// Learning Journal Goals API (standalone exports für direkten Import)
export const createLearningJournalGoal = async (goalData) => {
  try {
    // Nutze den neuen direkten Goals Endpoint
    const response = await api.post('/learning-journal/goals', goalData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    // Bewusst KEIN Fallback mehr: Frueher legte ein Fehler hier (429, 500, Timeout)
    // das Ziel still als Session-Ziel der juengsten Session an - auch einer laengst
    // abgeschlossenen. GoalsList wertete die Antwort trotzdem als Fehler, die
    // Schuelerin legte das Ziel erneut an, und im Forschungsexport stand ein fremd
    // zugeordnetes Session-Ziel (Audit A32). Die UI zeigt den Fehler selbst an.
    throw error;
  }
};

export const updateLearningJournalGoal = async (goalId, updateData) => {
  try {
    // Nutze den neuen direkten Goals Endpoint
    const response = await api.put(`/learning-journal/goals/${goalId}`, updateData);
    return response.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

export const deleteLearningJournalGoal = async (goalId) => {
  try {
    // Nutze den direkten Learning Journal Goals Endpoint
    // Dieser Endpoint unterstützt SOWOHL Session-bound ALS AUCH Standalone Goals
    const response = await api.delete(`/learning-journal/goals/${goalId}`);
    return response.data;
  } catch (error) {
    console.error('Fehler beim Löschen des Lernziels:', error);
    ErrorHandler.handle(error);
    throw error;
  }
};

// ===================== Goal Suggestions (AI) =====================

/**
 * Get AI-generated goal suggestions
 * @param {Object} context - Optional context for better suggestions
 * @param {boolean} refresh - Force refresh suggestions (bypass cache)
 */
export const getGoalSuggestions = async (context = {}, refresh = false) => {
  try {
    const params = new URLSearchParams();
    if (Object.keys(context).length > 0) {
      params.append('context', JSON.stringify(context));
    }
    if (refresh) {
      params.append('refresh', 'true');
    }

    const response = await api.get(`/learning-journal/goal-suggestions?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Fehler beim Abrufen der Goal Suggestions:', error);
    ErrorHandler.handle(error);

    // Fallback: Return placeholder suggestions if API fails
    return {
      success: true,
      data: [
        {
          id: 'placeholder-1',
          text: 'Neues Lernziel definieren',
          description: 'Setze dir ein konkretes Ziel für die nächste Woche',
          category: 'personal',
          priority: 'medium',
          source: 'default',
          confidence: 0.5
        },
        {
          id: 'placeholder-2',
          text: 'Lernfortschritt dokumentieren',
          description: 'Halte deine Erfolge und Herausforderungen fest',
          category: 'reflection',
          priority: 'low',
          source: 'default',
          confidence: 0.5
        }
      ],
      metadata: {
        generated: new Date(),
        context: {},
        fallback: true
      }
    };
  }
};

/**
 * Send feedback for a goal suggestion
 * @param {string} suggestionId - ID of the suggestion
 * @param {string} action - 'accepted', 'rejected', or 'modified'
 * @param {Object} additional - Additional data (rating, comment, goalData if accepted)
 */
export const sendGoalSuggestionFeedback = async (suggestionId, action, additional = {}) => {
  try {
    const payload = {
      suggestionId,
      action,
      ...additional
    };

    const response = await api.post('/learning-journal/goal-suggestions/feedback', payload);
    return response.data;
  } catch (error) {
    console.error('Fehler beim Senden des Suggestion Feedbacks:', error);
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * Get previously accepted goal suggestions
 */
export const getAcceptedGoalSuggestions = async () => {
  try {
    const response = await api.get('/learning-journal/goal-suggestions/accepted');
    return response.data;
  } catch (error) {
    console.error('Fehler beim Abrufen akzeptierter Suggestions:', error);
    ErrorHandler.handle(error);
    return { success: true, data: [] };
  }
};

export default api; 